import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Alert, AlertType, AlertStatus } from '@/types/community/alert';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  addDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; type?: string; status?: string; block?: { id?: string }; apartmentUnit?: { id?: string }; amenity?: { id?: string }; asset?: { id?: string } }>(
  data: T[],
  searchTerm?: string,
  type?: string,
  status?: string,
  blockId?: string,
  apartmentUnitId?: string,
  amenityId?: string,
  assetId?: string
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (type && item.type !== type) return false;
    if (status && item.status !== status) return false;
    if (blockId && item.block?.id !== blockId) return false;
    if (apartmentUnitId && item.apartmentUnit?.id !== apartmentUnitId) return false;
    if (amenityId && item.amenity?.id !== amenityId) return false;
    if (assetId && item.asset?.id !== assetId) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const ALERTS_COLLECTION = 'alerts';

// Types
export interface AlertsQueryParams {
  searchTerm?: string;
  type?: AlertType;
  status?: AlertStatus;
  blockId?: string;
  apartmentUnitId?: string;
  amenityId?: string;
  assetId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

type AlertWithDates = Partial<Alert>;

// Alerts API
export async function getAlerts({
  searchTerm,
  type,
  status,
  blockId,
  apartmentUnitId,
  amenityId,
  assetId,
  pageSize,
  page = 1,
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: AlertsQueryParams): Promise<{ alerts: Alert[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock alerts with filters', { searchTerm, type, status, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockAlerts = await loadMockData<Alert[]>('alerts', firestoreUser.language);
    const filteredData = filterMockData(mockAlerts, searchTerm, type, status, blockId, apartmentUnitId, amenityId, assetId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      alerts: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getAlerts', { searchTerm, type, status, blockId, apartmentUnitId, amenityId, assetId, pageSize, lastDoc, sortBy, sortOrder, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const alertsRef = collection(db, ALERTS_COLLECTION);
    
    // Simplified query - just filter by workspace
    const q = query(
      alertsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    const snapshot = await getDocs(q);

    const alerts = snapshot.docs
      .map(doc => {
        const data = doc.data() as AlertWithDates;
        const mapped: AlertWithDates = {
          ...data,
          id: doc.id,
          startTime: data.startTime ? formatFirestoreTimestamp(data.startTime) ?? undefined : undefined,
          endTime: data.endTime ? formatFirestoreTimestamp(data.endTime) ?? undefined : undefined,
          comments: data.comments || []
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.type === 'string' &&
          typeof mapped.status === 'string' &&
          mapped.startTime instanceof Date &&
          mapped.workspace && typeof mapped.workspace.id === 'string'
        ) {
          return mapped as Alert;
        }
        console.warn('Skipping invalid alert document:', doc.id, mapped);
        return undefined;
      })
      .filter((a): a is Alert => !!a);

    // Apply client-side filters
    let filteredAlerts = alerts;

    // Apply type filter
    if (type) {
      filteredAlerts = filteredAlerts.filter(alert => alert.type === type);
    }

    // Apply status filter
    if (status) {
      filteredAlerts = filteredAlerts.filter(alert => alert.status === status);
    }

    // Apply search filter
    if (searchTerm) {
      filteredAlerts = filteredAlerts.filter(alert => 
        alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting (client-side)
    if (sortBy === 'startTime') {
      filteredAlerts.sort((a, b) => {
        const aTime = a.startTime.getTime();
        const bTime = b.startTime.getTime();
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
      });
    } else {
      // Default sort by startTime desc
      filteredAlerts.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
    }

    // Apply pagination (client-side)
    if (pageSize && pageSize < filteredAlerts.length) {
      filteredAlerts = filteredAlerts.slice(0, pageSize);
    }

    return {
      alerts: filteredAlerts,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: filteredAlerts.length
    };
  } catch (error: any) {
    console.error('Error fetching alerts:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      firestoreError: error
    });
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to view alerts');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid query parameters: Please check your filters');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Query failed: Please try again');
    } else if (error.message) {
      throw new Error(`Failed to fetch alerts: ${error.message}`);
    } else {
      throw new Error('Failed to fetch alerts: Unknown error occurred');
    }
  }
}

export async function getAlertById(id: string, firestoreUser: User): Promise<Alert | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock alert with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockAlerts = await loadMockData<Alert[]>('alerts', firestoreUser.language);
    const alert = mockAlerts.find(a => a.id === id) || null;
    return alert;
  }
  
  console.log('API Call: getAlertById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const alertsRef = collection(db, ALERTS_COLLECTION);
    const q = query(
      alertsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const alertData = snapshot.docs[0].data() as AlertWithDates;
    const mappedAlert: AlertWithDates = {
      ...alertData,
      id: snapshot.docs[0].id,
      startTime: alertData.startTime ? formatFirestoreTimestamp(alertData.startTime) ?? undefined : undefined,
      endTime: alertData.endTime ? formatFirestoreTimestamp(alertData.endTime) ?? undefined : undefined,
      comments: alertData.comments || []
    };
    if (
      typeof mappedAlert.title === 'string' &&
      typeof mappedAlert.description === 'string' &&
      typeof mappedAlert.type === 'string' &&
      typeof mappedAlert.status === 'string' &&
      mappedAlert.startTime instanceof Date &&
      mappedAlert.workspace && typeof mappedAlert.workspace.id === 'string'
    ) {
      return mappedAlert as Alert;
    }
    return null;
  } catch (error) {
    console.error('Error fetching alert:', error);
    throw new Error('Failed to fetch alert');
  }
}

export async function addAlert(
  alert: Omit<Alert, 'id'>,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addAlert', { alert, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Validate required fields
    if (!alert.title?.trim()) {
      throw new Error('Alert title is required');
    }
    if (!alert.description?.trim()) {
      throw new Error('Alert description is required');
    }
    if (!alert.type) {
      throw new Error('Alert type is required');
    }
    if (!alert.status) {
      throw new Error('Alert status is required');
    }
    if (!alert.startTime) {
      throw new Error('Alert start time is required');
    }

    const alertData = addWorkspaceAndActivity(
      {
        ...alert,
        startTime: alert.startTime,
        endTime: alert.endTime,
        block: alert.block || undefined,
        apartmentUnit: alert.apartmentUnit || undefined,
        amenity: alert.amenity || undefined,
        asset: alert.asset || undefined,
        attachments: alert.attachments || undefined,
        activityRecords: alert.activityRecords || undefined
      },
      firestoreUser
    );

    const docRef = await addDoc(collection(db, ALERTS_COLLECTION), alertData);
    return docRef.id;
  } catch (error: any) {
    console.error('Error adding alert:', error);
    throw new Error(`Failed to add alert: ${error.message}`);
  }
}

export async function updateAlert(
  id: string,
  data: Partial<Omit<Alert, 'id'>>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateAlert', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get existing alert to create activity record
    const existingAlert = await getAlertById(id, firestoreUser);
    if (!existingAlert) {
      throw new Error('Alert not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    const updateData = removeUndefined({
      ...data,
      startTime: data.startTime,
      endTime: data.endTime,
      block: data.block || undefined,
      apartmentUnit: data.apartmentUnit || undefined,
      amenity: data.amenity || undefined,
      asset: data.asset || undefined,
      attachments: data.attachments || undefined,
      comments: data.comments !== undefined ? data.comments : existingAlert.comments,
      activityRecords: [...(existingAlert.activityRecords || []), activityRecord]
    });

    const alertsRef = collection(db, ALERTS_COLLECTION);
    const q = query(
      alertsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Alert not found');
    }

    await updateDoc(snapshot.docs[0].ref, updateData);
  } catch (error) {
    console.error('Error updating alert:', error);
    throw new Error('Failed to update alert');
  }
}

export async function deleteAlert(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteAlert', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const alertsRef = collection(db, ALERTS_COLLECTION);
    const q = query(
      alertsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Alert not found');
    }

    await deleteDoc(snapshot.docs[0].ref);
  } catch (error) {
    console.error('Error deleting alert:', error);
    throw new Error('Failed to delete alert');
  }
}

export async function getAlertsByType(type: AlertType, firestoreUser: User): Promise<Alert[]> {
  console.log('API Call: getAlertsByType', { type, firestoreUser });
  
  const { alerts } = await getAlerts({ type, firestoreUser });
  return alerts;
}

export async function getAlertsByStatus(status: AlertStatus, firestoreUser: User): Promise<Alert[]> {
  console.log('API Call: getAlertsByStatus', { status, firestoreUser });
  
  const { alerts } = await getAlerts({ status, firestoreUser });
  return alerts;
} 